import { sample } from 'effector';
import { spread } from 'patronum';
import queryString from 'query-string';
import {
  $centerHomeFilters,
  $chCities,
  $chComplexes,
  $chDistricts,
  $chHexagons,
  $chPoints,
  $chQuery,
  $chStartHexagons,
  $chStartPoints,
  $hexProp,
  $mobilePopup,
  $priceType,
} from './stores.js';
import {
  applyFiltersEv,
  applyQueryFiltersEv,
  changeCenterHomeFiltersEv,
  changeHexPropEv,
  changeMobilePopupEv,
  getCHCitiesEv,
  getCHComplexesEv,
  getCHDistrictsEv,
  getCHHexagonsEv,
  initCHStoreEv,
  resetCenterHomeFiltersEv,
  resetMobilePopupEv,
  togglePriceTypeEv,
} from './events.js';
import { filterPoints } from './utils.js';
import ch_hexagons from '../../data/ch_hexagons.json';
import { calculateThresholdsEv } from '../gradientModel/index.js';
import { flyTo } from '../../utils/mapbox-utils.js';
import {
  getCHCitiesFx,
  getCHComplexesFx,
  getCHDistrictsFx,
  getCHHexagonsFx,
  getCHPointsFx,
} from './effects.js';

const city_centers = {
  1: [71.4294498778861, 51.12251810045831],
  2: [76.91537388145935, 43.21269175727281],
  3: [73.1079485904697, 49.79483052208329],
};

$centerHomeFilters.reset(resetCenterHomeFiltersEv);

$hexProp.on(changeHexPropEv, (state, payload) => payload);

$priceType.on(togglePriceTypeEv, (state, payload) => {
  if (payload) return 'absolute';
  return 'meter';
});

$mobilePopup
  .on(changeMobilePopupEv, (state, payload) => payload)
  .reset(resetMobilePopupEv);

sample({
  source: $centerHomeFilters,
  clock: changeCenterHomeFiltersEv,
  fn: (source, clock) => {
    if (clock.field === 'rooms' || clock.field === 'house_type') {
      if (source[clock.field].includes(clock.value)) {
        return {
          ...source,
          [clock.field]: source[clock.field].filter(
            (item) => item !== clock.value
          ),
        };
      }
      return {
        ...source,
        [clock.field]: [...source[clock.field], clock.value],
      };
    }
    return {
      ...source,
      [clock.field]: clock.value,
    };
  },
  target: $centerHomeFilters,
});

sample({
  source: [$chStartPoints, $centerHomeFilters, $chComplexes],
  clock: applyFiltersEv,
  fn: ([points, filters, complexes]) => {
    flyTo(city_centers[filters.city], 10);
    return filterPoints(points, filters, complexes);
  },
  target: $chPoints,
});

sample({
  clock: initCHStoreEv,
  fn: () => {
    if (!window.location.search.includes('city_id')) {
      setTimeout(() => {
        flyTo([76.91537388145935, 43.21269175727281], 10);
      }, 3000);
    }
  },
  target: [
    // $chHexagons,
    // calculateThresholdsEv,
    getCHHexagonsEv,
    getCHPointsFx,
    getCHComplexesEv,
    getCHCitiesEv,
    getCHDistrictsEv,
  ],
});

sample({
  source: [$chHexagons, $priceType],
  clock: $hexProp.updates,
  fn: ([hexagons, priceType], clock) => {
    return hexagons.map((item) => {
      return {
        ...item,
        properties: {
          ...item.properties,
          index_main:
            item.properties[
              `${clock}${priceType === 'absolute' ? '_full' : ''}`
            ],
        },
      };
    });
  },
  target: [$chHexagons, calculateThresholdsEv],
});

sample({
  source: [$chHexagons, $hexProp],
  clock: $priceType.updates,
  fn: ([hexagons, hexProp], priceType) => {
    return hexagons.map((item) => {
      return {
        ...item,
        properties: {
          ...item.properties,
          index_main:
            item.properties[
              `${hexProp}${priceType === 'absolute' ? '_full' : ''}`
            ],
        },
      };
    });
  },
  target: [$chHexagons, calculateThresholdsEv],
});

sample({
  source: $chStartPoints,
  clock: resetCenterHomeFiltersEv,
  fn: (source) => {
    flyTo(city_centers[2], 10);
    return source.filter((item) => item.properties.city_id === 2);
  },
  target: $chPoints,
});

sample({
  source: [$hexProp, $priceType, $centerHomeFilters, $chStartHexagons],
  clock: applyFiltersEv,
  fn: ([hexProp, priceType, filters, startHexagons], _) => {
    return startHexagons
      .filter((item) => item.properties.city === filters.city)
      .map((item) => {
        return {
          ...item,
          properties: {
            ...item.properties,
            index_main:
              item.properties[
                `${hexProp}${priceType === 'absolute' ? '_full' : ''}`
              ],
          },
        };
      });
  },
  target: [$chHexagons, calculateThresholdsEv],
});

sample({
  source: [$hexProp, $priceType, $chStartHexagons],
  clock: resetCenterHomeFiltersEv,
  fn: ([hexProp, priceType, startHexagons], _) => {
    return startHexagons
      .filter((item) => item.properties.city === 2)
      .map((item) => {
        return {
          ...item,
          properties: {
            ...item.properties,
            index_main:
              item.properties[
                `${hexProp}${priceType === 'absolute' ? '_full' : ''}`
              ],
          },
        };
      });
  },
  target: [$chHexagons, calculateThresholdsEv],
});

sample({
  clock: getCHPointsFx.doneData,
  fn: (points) => {
    const curr_points = points.features.filter(
      (item) => item.properties.city_id === 2
    );
    return {
      startPoints: points.features,
      points: curr_points,
    };
  },
  target: spread({
    targets: {
      startPoints: $chStartPoints,
      points: [$chPoints, applyQueryFiltersEv],
    },
  }),
});

sample({
  source: $chQuery,
  clock: applyQueryFiltersEv,
  filter: (query) => query !== '' && !query.includes('property_id'),
  fn: (query, clock) => {
    const queryFilters = queryString.parse(query);
    const complexes = [];
    const rooms = [];
    const condition = [];
    const house_type = [];
    const districts = [];

    Object.keys(queryFilters).forEach((key) => {
      if (key.includes('complex')) {
        complexes.push(queryFilters[key]);
      }
      if (key.includes('rooms')) {
        rooms.push(queryFilters[key]);
      }
      if (key.includes('condition')) {
        condition.push(queryFilters[key]);
      }
      if (key.includes('house_type')) {
        house_type.push(queryFilters[key]);
      }
      if (key.includes('districts')) {
        districts.push(queryFilters[key]);
      }
    });
    return {
      filters: {
        city: parseInt(queryFilters.city_id) || 2,
        residential_complex: complexes.map((item) => parseInt(item)) || [],
        from_owner: queryFilters.seller === 'owner' || false,
        mortgage: queryFilters.available_for_mortgage === '1' || false,
        is_in_pledge_bank: queryFilters.is_in_pledge_bank === '1' || false,
        tour_3d: false,
        rooms:
          rooms.map((item) => (item.includes('4+') ? '4+' : parseInt(item))) ||
          [],
        cost_from: parseInt(queryFilters.price_from) || null,
        cost_to: parseInt(queryFilters.price_to) || null,
        renovation_level: condition || [],
        house_type: house_type || [],
        square_from: parseInt(queryFilters.area_from) || null,
        square_to: parseInt(queryFilters.area_to) || null,
        year_from: parseInt(queryFilters.year_from) || null,
        year_to: parseInt(queryFilters.year_to) || null,
        first_floor: queryFilters.not_first_floor === '1' || false,
        districts: districts || [],
      },
      complexes: parseInt(queryFilters.city_id) || 2,
    };
  },
  target: spread({
    targets: {
      filters: [$centerHomeFilters, applyFiltersEv],
      complexes: getCHComplexesEv,
    },
  }),
});

sample({
  source: [$chQuery, $chStartPoints, $chStartHexagons],
  clock: applyQueryFiltersEv,
  filter: ([query]) => query !== '' && query.includes('property_id'),
  fn: ([query, start_points, start_hexagons], clock) => {
    const parsed_query = queryString.parse(query);
    const feature = start_points.find(
      (item) => item.properties.id === parseInt(parsed_query.property_id)
    );
    flyTo(city_centers[feature.properties.city_id], 10);
    const hexagons = start_hexagons.filter((item) => {
      return item.properties.city === feature.properties.city_id;
    });
    if (parsed_query.rooms) {
      return {
        points: [feature],
        hexagons,
        hexProp: `room${parsed_query.rooms}_hex_mean`,
      };
    }
    return {
      points: [feature],
      hexagons,
    };
  },
  target: spread({
    targets: {
      points: $chPoints,
      hexagons: [$chHexagons, calculateThresholdsEv],
      hexProp: $hexProp,
    },
  }),
});

sample({
  clock: getCHComplexesEv,
  fn: (clock) => {
    if (typeof clock === 'number') return clock;
    return 2;
  },
  target: getCHComplexesFx,
});

sample({
  clock: getCHCitiesEv,
  target: getCHCitiesFx,
});

sample({
  clock: getCHDistrictsEv,
  target: getCHDistrictsFx,
});

sample({
  clock: getCHDistrictsFx.doneData,
  fn: (clock) => {
    return clock.districts.map((item) => {
      return {
        ...item,
        label: item.title,
        value: item.id,
      };
    });
  },
  target: $chDistricts,
});

sample({
  clock: getCHComplexesFx.doneData,
  fn: (clock) =>
    clock.complexes.map((item) => {
      return {
        label: item.title,
        value: item.id,
      };
    }),
  target: $chComplexes,
});

sample({
  clock: getCHCitiesFx.doneData,
  fn: (clock) =>
    clock.cities.map((item) => {
      return {
        label: item.title,
        value: item.id,
      };
    }),
  target: $chCities,
});

sample({
  clock: changeCenterHomeFiltersEv,
  filter: (clock) => clock.field === 'city',
  fn: (clock) => {
    return Number(clock.value);
  },
  target: [getCHComplexesFx, getCHDistrictsFx],
});

sample({
  clock: getCHHexagonsEv,
  target: getCHHexagonsFx,
});

sample({
  clock: getCHHexagonsFx.doneData,
  fn: (clock) => {
    const startHex = clock.map((item) => {
      const feature = ch_hexagons.features.find(
        (f) => f.properties.zoom9_id === item.zoom9_id
      );
      return {
        ...feature,
        properties: {
          ...item,
          index_main: item.room1_hex_mean,
        },
      };
    });

    const hex = startHex.filter((item) => item.properties.city === 2);
    return {
      startHex,
      hex,
    };
  },
  target: spread({
    targets: {
      startHex: $chStartHexagons,
      hex: [$chHexagons, calculateThresholdsEv],
    },
  }),
});
